import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import Container from '@amzn/awsui-components-react/polaris/container';
import Button from '@amzn/awsui-components-react/polaris/button';
import DatePicker from '@amzn/awsui-components-react/polaris/date-picker';
import FormField from '@amzn/awsui-components-react/polaris/form-field';
import Grid from '@amzn/awsui-components-react/polaris/grid';
import Header from '@amzn/awsui-components-react/polaris/header';
import Input from '@amzn/awsui-components-react/polaris/input';
import Link from '@amzn/awsui-components-react/polaris/link';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import RadioGroup from '@amzn/awsui-components-react/polaris/radio-group';
import Select, { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';

import { getOptionFromValue } from '../../../utils/url';

import { EditableInfoField } from '../../../hooks/events/editableEventInfo';
import { useShiftFromUrlParams } from '../../../hooks/shifts/shiftFromUrlParams';
import { isUnlistedEvent } from '../../../utils/eventUtils';
import { useTimezone } from '../../../hooks/timezone';
import { allTimezoneWithOffsetOptions } from '../../../data/timezoneOptions';
import { featureIsEnabled, useFeatures } from '../../../hooks/features';
import { EVENT_CREATION } from '../../../utils/constants';
import { externalLinks } from '../../../data/externalLinks';
import { LocationAutosuggest } from './locationAutosuggest';

export type EditEventInfoProps = {
  editableInfo: { [s in EditableInfoField]: string };
  updateEditableInfoFields: (fields: { [s in EditableInfoField]?: string }) => void;
  eventErrors?: { [s in EditableInfoField]?: string };
  eventWarnings?: { [s in EditableInfoField]?: string };
};

export function EditEventInfo(props: EditEventInfoProps) {
  const namespaces = ['attendance', 'events', 'translation'];
  const { t } = useTranslation(namespaces);
  const {
    editableInfo,
    updateEditableInfoFields,
    eventErrors: errors,
    eventWarnings: warnings,
  } = props;

  const { event, eventId, shiftId } = useShiftFromUrlParams();

  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const showGrantFields = featureIsEnabled(EVENT_CREATION, features, searchParams);

  const locationType = t('events:locationType', { returnObjects: true }) as SelectProps.Options;
  const focus = t('events:focus', { returnObjects: true }) as SelectProps.Options;
  const activity = t('events:activity', { returnObjects: true }) as SelectProps.Options;

  const isUnlistedEventTracking =
    (!eventId && !shiftId) || (event != null && isUnlistedEvent(event));

  // https://t.corp.amazon.com/D499882019
  // Surface the timezone the selected date is recorded in so coordinators
  // understand which day they are tracking. Mirrors the timezone display
  // in the create-event flow. Uses the same zone the event is saved with.
  const timezone = useTimezone();
  const timezoneLabel =
    allTimezoneWithOffsetOptions.find((tz) => tz.id === timezone)?.text ?? timezone;

  return (
    <Container header={<Header variant="h3">{t('editEventInfo.header')}</Header>}>
      <SpaceBetween direction="vertical" size="l">
        {isUnlistedEventTracking ? (
          <>
            <FormField
              label={t('editEventInfo.title')}
              description={t('editEventInfo.titleDescription')}
              stretch
              errorText={errors?.title}
            >
              <Input
                value={editableInfo.title}
                onChange={({ detail }) => updateEditableInfoFields({ title: detail.value })}
              />
            </FormField>
            <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
              <FormField
                label={t('editEventInfo.date')}
                constraintText={t('editEventInfo.dateTimezoneHint', { timezone: timezoneLabel })}
                stretch
                errorText={errors?.date}
              >
                <DatePicker
                  value={editableInfo.date}
                  onChange={({ detail }) => updateEditableInfoFields({ date: detail.value })}
                />
              </FormField>
              <FormField
                stretch
                warningText={warnings?.length}
                errorText={errors?.length}
                label={
                  <SpaceBetween direction="horizontal" size="l">
                    <span>{t('editEventInfo.length')}</span>
                    <Popover
                      dismissButton={false}
                      position="top"
                      size="small"
                      triggerType="custom"
                      content={t('editEventInfo.lengthInfo')}
                    >
                      <Button variant="inline-link">
                        {t('grant.infoButton', { ns: 'translation' })}
                      </Button>
                    </Popover>
                  </SpaceBetween>
                }
              >
                <Input
                  onChange={({ detail }) => {
                    // Strip out anything other than digits or :
                    const rawTime = (detail.value.match(/[\d:]/g) || []).join('');
                    updateEditableInfoFields({ length: rawTime });
                  }}
                  value={editableInfo.length}
                  placeholder="hh:mm"
                  ariaRequired
                  inputMode="numeric"
                  step={1}
                />
              </FormField>
            </Grid>
            <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
              <FormField
                label={t('editEventInfo.locationType')}
                description={t('editEventInfo.locationDescription')}
                stretch
                errorText={errors?.activity_location_type}
              >
                <Select
                  onChange={({ detail }) =>
                    updateEditableInfoFields({
                      activity_location_type: detail.selectedOption.value || '',
                      offsite_free_text:
                        detail.selectedOption.value === editableInfo.activity_location_type
                          ? editableInfo.offsite_free_text
                          : '',
                      building_code:
                        detail.selectedOption.value === editableInfo.activity_location_type
                          ? editableInfo.building_code
                          : '',
                    })
                  }
                  selectedOption={getOptionFromValue(
                    editableInfo.activity_location_type,
                    locationType
                  )}
                  options={locationType}
                />
              </FormField>
              {editableInfo.activity_location_type &&
              editableInfo.activity_location_type !== 'virtual' ? (
                <FormField
                  label={
                    editableInfo.activity_location_type === 'off-campus'
                      ? t('editEventInfo.offsite_free_text')
                      : t('editEventInfo.building_code')
                  }
                  description={
                    editableInfo.activity_location_type === 'off-campus'
                      ? t('editEventInfo.offsiteLocationDescription')
                      : t('editEventInfo.buildingCodeDescription')
                  }
                  stretch
                  errorText={errors?.offsite_free_text}
                >
                  <LocationAutosuggest
                    currentLocation={
                      editableInfo.activity_location_type === 'off-campus'
                        ? editableInfo.offsite_free_text
                        : editableInfo.building_code
                    }
                    onChange={({ detail }) =>
                      updateEditableInfoFields({
                        offsite_free_text:
                          editableInfo.activity_location_type === 'off-campus'
                            ? detail.value
                            : editableInfo.offsite_free_text,
                        building_code:
                          editableInfo.activity_location_type === 'on-campus'
                            ? detail.value
                            : editableInfo.building_code,
                      })
                    }
                    eventType={
                      editableInfo.activity_location_type === 'off-campus'
                        ? 'off-campus'
                        : 'on-campus'
                    }
                    error={
                      editableInfo.activity_location_type === 'off-campus'
                        ? errors?.offsite_free_text
                        : errors?.building_code
                    }
                  />
                </FormField>
              ) : null}
            </Grid>
          </>
        ) : null}
        <Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
          <FormField
            label={t('editEventInfo.focus')}
            description={t('editEventInfo.focusDescription')}
            stretch
            errorText={errors?.focus}
          >
            <Select
              onChange={({ detail }) => {
                updateEditableInfoFields({ focus: detail.selectedOption.value || '' });
              }}
              selectedOption={getOptionFromValue(editableInfo.focus, focus)}
              options={focus}
            />
          </FormField>
          <FormField
            label={t('editEventInfo.activity')}
            description={t('editEventInfo.activityDescription')}
            stretch
            errorText={errors?.activity}
          >
            <Select
              onChange={({ detail }) =>
                updateEditableInfoFields({ activity: detail.selectedOption.value || '' })
              }
              selectedOption={getOptionFromValue(editableInfo.activity, activity)}
              options={activity}
            />
          </FormField>
        </Grid>
        {showGrantFields && (
          <SpaceBetween direction="vertical" size="l">
            <FormField
              label={
                <SpaceBetween direction="horizontal" size="l">
                  <span>{t('grant.question', { ns: 'translation' })}</span>
                  <Popover
                    dismissButton={false}
                    position="top"
                    size="large"
                    triggerType="custom"
                    header={t('grant.question', { ns: 'translation' })}
                    content={
                      <SpaceBetween direction="vertical" size="s">
                        <span style={{ whiteSpace: 'pre-line' }}>
                          {t('grant.info.description', { ns: 'translation' })}
                        </span>
                        <Link href={externalLinks.volunteeringGrants} external>
                          {t('grant.info.linkText', { ns: 'translation' })}
                        </Link>
                      </SpaceBetween>
                    }
                  >
                    <Button variant="inline-link">
                      {t('grant.infoButton', { ns: 'translation' })}
                    </Button>
                  </Popover>
                </SpaceBetween>
              }
              description={t('grant.questionSubText', { ns: 'translation' })}
              errorText={errors?.used_aci_grant}
            >
              <RadioGroup
                onChange={({ detail }) =>
                  updateEditableInfoFields({
                    used_aci_grant: detail.value,
                    grant_id: detail.value === 'true' ? editableInfo.grant_id : '',
                  })
                }
                value={editableInfo.used_aci_grant || ''}
                items={[
                  { value: 'true', label: t('grant.yes', { ns: 'translation' }) },
                  { value: 'false', label: t('grant.no', { ns: 'translation' }) },
                ]}
              />
            </FormField>
            {editableInfo.used_aci_grant === 'true' && (
              <FormField
                label={t('grant.id', { ns: 'translation' })}
                description={t('grant.idSubText', { ns: 'translation' })}
                errorText={errors?.grant_id}
                warningText={warnings?.grant_id}
              >
                <Input
                  placeholder={t('grant.idPlaceholder', { ns: 'translation' })}
                  value={editableInfo.grant_id}
                  onChange={({ detail }) => updateEditableInfoFields({ grant_id: detail.value })}
                />
              </FormField>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
}
